import React, { useState, useEffect } from 'react';
import { Users, Plus, X, Package, Archive, Camera, Edit2, Briefcase, Clock } from 'lucide-react';
import api, { API_URL, getServerUrl } from '../../services/api';
import CoffresOuvriers from './CoffresOuvriers';
import SuiviOuvriersProduction from './SuiviOuvriersProduction';

function OuvriersComplet() {
  const [activeTab, setActiveTab] = useState('liste');
  const [ouvriers, setOuvriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOuvrier, setSelectedOuvrier] = useState(null);
  const [showCoffresModal, setShowCoffresModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [formOuvrier, setFormOuvrier] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    specialite: 'polyvalent',
    prix_minute: '',
    actif: true,
    date_embauche: '',
    notes: ''
  });

  useEffect(() => {
    loadOuvriers();
  }, []);

  const loadOuvriers = async () => {
    try {
      const response = await api.get(`${API_URL}/ouvriers?actif=false`);
      setOuvriers(response.data || []);
    } catch (error) {
      console.error('Erreur:', error);
      setOuvriers([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (ouvrierId, file) => {
    if (!file) return;
    
    setUploadingPhoto(ouvrierId);
    
    try {
      const formData = new FormData();
      formData.append('photo', file);
      
      await api.post(`${API_URL}/ouvriers/${ouvrierId}/photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      alert('Photo mise à jour avec succès');
      await loadOuvriers();
    } catch (error) {
      console.error('Erreur upload photo:', error);
      alert('Erreur lors de l\'upload de la photo');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleEditOuvrier = (ouvrier) => {
    setSelectedOuvrier(ouvrier);
    setFormOuvrier({
      nom: ouvrier.nom || '',
      prenom: ouvrier.prenom || '',
      telephone: ouvrier.telephone || '',
      email: ouvrier.email || '',
      specialite: ouvrier.specialite || 'polyvalent',
      prix_minute: ouvrier.prix_minute || '',
      actif: ouvrier.actif !== false,
      date_embauche: ouvrier.date_embauche || '',
      notes: ouvrier.notes || ''
    });
    setShowEditModal(true);
  };

  const handleSaveOuvrier = async () => {
    try {
      if (selectedOuvrier) {
        await api.put(`${API_URL}/ouvriers/${selectedOuvrier.id}`, formOuvrier);
        alert('Ouvrier modifié avec succès');
      } else {
        await api.post(`${API_URL}/ouvriers`, formOuvrier);
        alert('Ouvrier créé avec succès');
      }
      
      setShowEditModal(false);
      setSelectedOuvrier(null);
      setFormOuvrier({
        nom: '',
        prenom: '',
        telephone: '',
        email: '',
        specialite: 'polyvalent',
        prix_minute: '',
        actif: true,
        date_embauche: '',
        notes: ''
      });
      await loadOuvriers();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const getSpecialiteIcon = (specialite) => {
    const icons = {
      'sertisseur': '💎',
      'polisseur': '✨',
      'monteur': '🔧',
      'graveur': '✍️',
      'emailleur': '🎨',
      'designer': '📐',
      'controle_qualite': '✅',
      'polyvalent': '🌟'
    };
    return icons[specialite] || '👨‍🔧';
  };

  const getStatutColor = (actif) => {
    return actif ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getStatutLabel = (actif) => {
    return actif ? 'Actif' : 'Inactif';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <Users className="h-8 w-8 text-indigo-600" />
            <h1 className="text-3xl font-bold text-gray-800">Gestion des Ouvriers</h1>
          </div>
          
          <button
            onClick={() => {
              setSelectedOuvrier(null);
              setFormOuvrier({
                nom: '',
                prenom: '',
                telephone: '',
                email: '',
                specialite: 'polyvalent',
                prix_minute: '',
                actif: true,
                date_embauche: '',
                notes: ''
              });
              setShowEditModal(true);
            }}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg flex items-center space-x-2 font-semibold"
          >
            <Plus className="h-5 w-5" />
            <span>Nouvel Ouvrier</span>
          </button>
        </div>

        {/* Onglets */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('liste')}
              className={`flex-1 px-6 py-4 font-semibold transition-all flex items-center justify-center gap-2 ${
                activeTab === 'liste'
                  ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Users className="h-5 w-5" />
              Liste des Ouvriers
            </button>
            
            <button
              onClick={() => setActiveTab('coffres')}
              className={`flex-1 px-6 py-4 font-semibold transition-all flex items-center justify-center gap-2 ${
                activeTab === 'coffres'
                  ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Archive className="h-5 w-5" />
              Coffres & Stock
            </button>
            
            <button
              onClick={() => setActiveTab('production')}
              className={`flex-1 px-6 py-4 font-semibold transition-all flex items-center justify-center gap-2 ${
                activeTab === 'production'
                  ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Briefcase className="h-5 w-5" />
              Suivi Production
            </button>
          </div>
        </div>

        {/* Contenu selon l'onglet actif */}
        {activeTab === 'liste' && (
          <div>
            {loading ? (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <div className="text-xl text-gray-600">Chargement...</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {ouvriers.map((ouvrier) => (
                  <div key={ouvrier.id} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden">
                    {/* En-tête carte avec photo */}
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white">
                      <div className="flex items-start gap-4">
                        {/* Photo de l'ouvrier */}
                        <div className="relative group">
                          {ouvrier.photo_url ? (
                            <img
                              src={`${API_URL.replace("/api","")}${ouvrier.photo_url}`}
                              alt={`${ouvrier.prenom} ${ouvrier.nom}`}
                              className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-lg"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center border-2 border-white shadow-lg">
                              <span className="text-2xl font-bold">{ouvrier.prenom?.charAt(0)}{ouvrier.nom?.charAt(0)}</span>
                            </div>
                          )}
                          
                          {/* Bouton upload photo */}
                          <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                            {uploadingPhoto === ouvrier.id ? (
                              <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                            ) : (
                              <Camera className="h-6 w-6 text-white" />
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handlePhotoUpload(ouvrier.id, e.target.files[0])}
                              disabled={uploadingPhoto === ouvrier.id}
                            />
                          </label>
                        </div>
                        
                        {/* Infos ouvrier */}
                        <div className="flex-1">
                          <div className="text-xl font-bold">
                            {ouvrier.prenom} {ouvrier.nom}
                          </div>
                          <div className="text-sm opacity-90 capitalize">{ouvrier.specialite}</div>
                        </div>
                        
                        <div className="text-4xl">{getSpecialiteIcon(ouvrier.specialite)}</div>
                      </div>
                    </div>

                    {/* Corps carte */}
                    <div className="p-4 space-y-3">
                      {/* Statut */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Statut</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatutColor(ouvrier.actif)}`}>
                          {getStatutLabel(ouvrier.actif)}
                        </span>
                      </div>

                      {/* Taux */}
                      {ouvrier.prix_minute && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Taux/minute</span>
                          <span className="text-lg font-bold text-indigo-600">
                            {parseFloat(ouvrier.prix_minute).toFixed(2)} DH
                          </span>
                        </div>
                      )}

                      {/* Contact */}
                      {ouvrier.telephone && (
                        <div className="text-sm text-gray-600">
                          📞 {ouvrier.telephone}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="grid grid-cols-2 gap-2 pt-3 border-t">
                        <button 
                          onClick={() => handleEditOuvrier(ouvrier)}
                          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all text-sm flex items-center justify-center gap-1 shadow-md"
                        >
                          <Edit2 className="h-4 w-4" />
                          <span>Modifier</span>
                        </button>
                        
                        <button 
                          onClick={() => {
                            setSelectedOuvrier(ouvrier);
                            setShowCoffresModal(true);
                          }}
                          className="px-3 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all text-sm flex items-center justify-center gap-1 shadow-md"
                        >
                          <Archive className="h-4 w-4" />
                          <span>Coffre</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {ouvriers.length === 0 && !loading && (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <Users className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <p className="text-xl text-gray-500">Aucun ouvrier enregistré</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'coffres' && (
          <div className="bg-white rounded-lg shadow-md">
            <CoffresOuvriers />
          </div>
        )}

        {activeTab === 'production' && (
          <div className="bg-white rounded-lg shadow-md">
            <SuiviOuvriersProduction />
          </div>
        )}

        {/* Modal Coffres Ouvriers */}
        {showCoffresModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden">
              <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Archive className="h-6 w-6" />
                  Coffre de {selectedOuvrier?.prenom} {selectedOuvrier?.nom}
                </h2>
                <button
                  onClick={() => {
                    setShowCoffresModal(false);
                    setSelectedOuvrier(null);
                  }}
                  className="p-2 hover:bg-white/20 rounded-full transition-all"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
                <CoffresOuvriers preSelectedOuvrier={selectedOuvrier} />
              </div>
            </div>
          </div>
        )}

        {/* Modal Ajouter/Modifier Ouvrier */}
        {showEditModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl">
              <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Users className="h-6 w-6" />
                  {selectedOuvrier ? 'Modifier Ouvrier' : 'Nouvel Ouvrier'}
                </h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedOuvrier(null);
                  }}
                  className="p-2 hover:bg-white/20 rounded-full transition-all"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                    <input
                      type="text"
                      value={formOuvrier.prenom}
                      onChange={(e) => setFormOuvrier({...formOuvrier, prenom: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                    <input
                      type="text"
                      value={formOuvrier.nom}
                      onChange={(e) => setFormOuvrier({...formOuvrier, nom: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={formOuvrier.telephone}
                      onChange={(e) => setFormOuvrier({...formOuvrier, telephone: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={formOuvrier.email}
                      onChange={(e) => setFormOuvrier({...formOuvrier, email: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Spécialité *</label>
                    <select
                      value={formOuvrier.specialite}
                      onChange={(e) => setFormOuvrier({...formOuvrier, specialite: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="sertisseur">💎 Sertisseur</option>
                      <option value="polisseur">✨ Polisseur</option>
                      <option value="monteur">🔧 Monteur</option>
                      <option value="graveur">✍️ Graveur</option>
                      <option value="emailleur">🎨 Émailleur</option>
                      <option value="designer">📐 Designer</option>
                      <option value="controle_qualite">✅ Contrôle Qualité</option>
                      <option value="polyvalent">🌟 Polyvalent</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prix/minute (DH)</label>
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      step="0.01"
                      value={formOuvrier.prix_minute}
                      onChange={(e) => setFormOuvrier({...formOuvrier, prix_minute: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date d'embauche</label>
                    <input
                      type="date"
                      value={formOuvrier.date_embauche}
                      onChange={(e) => setFormOuvrier({...formOuvrier, date_embauche: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={formOuvrier.actif}
                      onChange={(e) => setFormOuvrier({...formOuvrier, actif: e.target.value === 'true'})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="true">Actif</option>
                      <option value="false">Inactif</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formOuvrier.notes}
                    onChange={(e) => setFormOuvrier({...formOuvrier, notes: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedOuvrier(null);
                  }}
                  className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold transition-all"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveOuvrier}
                  className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition-all"
                >
                  {selectedOuvrier ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OuvriersComplet;
